import { useRef, useState } from "react";

import { SubmitHandler, useForm } from "react-hook-form";
import { Button, TextInput } from "components";
import closeImg from "assets/icons/icon-close.svg";

import "./VisitBookingPopup.scss";
import { useIntl } from "react-intl";
import { useMutation } from "react-query";
import { publicRequest } from "utils";

interface VisitBookingPopupProps {
  isOpen: boolean;
  closePopup: () => void;
}

export type VisitBookingInputs = {
  firstName: string;
  lastName: string;
  phone: string;
};

export function VisitBookingPopup({
  isOpen,
  closePopup,
}: VisitBookingPopupProps) {
  const { formatMessage } = useIntl();

  const contentRef = useRef(null);
  const [isSent, setIsSent] = useState(false);

  const closeOnClickingOutside = (e: any) => {
    if (e?.target === contentRef?.current) {
      closePopup();
    }
  };

  const {
    formState: { errors },
    register,
    handleSubmit,
  } = useForm<VisitBookingInputs>({
    defaultValues: {
      firstName: "",
      lastName: "",
      phone: "",
    },
  });

  const handleVisitBooking = useMutation(
    (data: FormData) =>
      publicRequest({
        url: `/api/email/labcall`,
        method: "POST",
        body: data,
      }),
    {
      onSuccess: () => {
        setIsSent(true);
      },
      onError: (error) => {
        console.error(error);
      },
    }
  );

  const bookVisit: SubmitHandler<VisitBookingInputs> = async (data, e) => {
    e?.preventDefault();
    const formData = new FormData();
    formData.append("name", `${data.firstName} ${data.lastName}`.trim());
    formData.append("phone", data.phone);
    // The lab-call mailer reads these two keys as well, so they are always sent.
    formData.append("email", "");
    formData.append(
      "address",
      formatMessage({
        id: "laboratory.visit.request-label",
        defaultMessage: "ვიზიტის დაჯავშნა (ლაბორატორია)",
      })
    );
    formData.append(
      "title",
      formatMessage({
        id: "laboratory.visit.request-label",
        defaultMessage: "ვიზიტის დაჯავშნა (ლაბორატორია)",
      })
    );
    handleVisitBooking.mutate(formData);
  };

  const requiredMessage = formatMessage({
    id: "laboratory.visit.required",
    defaultMessage: "ველის შევსება სავალდებულოა",
  });

  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={contentRef}
      className="VisitBookingPopup"
      onClick={(e) => closeOnClickingOutside(e)}
    >
      <form
        className="VisitBookingPopup_content"
        onSubmit={handleSubmit(bookVisit)}
      >
        <div className="VisitBookingPopup_header VisitBookingPopup_row">
          <h3>
            {formatMessage({
              id: "laboratory.book-visit",
              defaultMessage: "დაჯავშნე ვიზიტი",
            })}
          </h3>
          <div>
            <img
              src={closeImg}
              alt="closing icon"
              className="VisitBookingPopup_closeBtn"
              onClick={() => {
                closePopup();
              }}
              loading="lazy"
            />
          </div>
        </div>

        {isSent ? (
          <>
            <p className="VisitBookingPopup_message VisitBookingPopup_message--success">
              {formatMessage({
                id: "laboratory.visit.success",
                defaultMessage:
                  "თქვენი მონაცემები წარმატებით გაიგზავნა, ოპერატორი მალე დაგიკავშირდებათ",
              })}
            </p>
            <div className="VisitBookingPopup_buttons">
              <Button
                type="button"
                className="VisitBookingPopup_activeBtn"
                onClick={() => {
                  closePopup();
                }}
              >
                {formatMessage({
                  id: "laboratory.visit.close",
                  defaultMessage: "დახურვა",
                })}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="VisitBookingPopup_row">
              <TextInput
                placeholder={formatMessage({
                  id: "laboratory.visit.name",
                  defaultMessage: "სახელი",
                })}
                {...register("firstName", { required: requiredMessage })}
                error={errors?.firstName && true}
                errorMessage={errors.firstName?.message}
              />
            </div>
            <div className="VisitBookingPopup_row">
              <TextInput
                placeholder={formatMessage({
                  id: "laboratory.visit.lastname",
                  defaultMessage: "გვარი",
                })}
                {...register("lastName", { required: requiredMessage })}
                error={errors?.lastName && true}
                errorMessage={errors.lastName?.message}
              />
            </div>
            <div className="VisitBookingPopup_row">
              <TextInput
                placeholder={formatMessage({
                  id: "laboratory.visit.phone",
                  defaultMessage: "ტელეფონის ნომერი",
                })}
                {...register("phone", { required: requiredMessage })}
                error={errors?.phone && true}
                errorMessage={errors.phone?.message}
              />
            </div>

            {handleVisitBooking.isError && (
              <p className="VisitBookingPopup_message VisitBookingPopup_message--error">
                {formatMessage({
                  id: "laboratory.visit.error",
                  defaultMessage:
                    "მონაცემების გაგზავნა ვერ მოხერხდა. გთხოვთ, სცადოთ ხელახლა",
                })}
              </p>
            )}

            <div className="VisitBookingPopup_buttons">
              <Button
                type="button"
                className="VisitBookingPopup_inActiveBtn"
                onClick={() => {
                  closePopup();
                }}
              >
                {formatMessage({
                  id: "laboratory.back",
                  defaultMessage: "უკან",
                })}
              </Button>
              <Button
                type="submit"
                className="VisitBookingPopup_activeBtn"
                disabled={handleVisitBooking.isLoading}
              >
                {formatMessage({
                  id: "laboratory.visit.submit",
                  defaultMessage: "მონაცემების გაგზავნა",
                })}
              </Button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
